import type { ChangeSubtask, ChangeTypePolicyFlags, ChangeWorkGroup } from '../../types/changes';
import { isInfraIncidentArea } from './changeInfraIncidentAreas';
import { policyFlagsForType } from './changeTypePolicy';
import { defaultIncidentWorkflowId, type IncidentWorkflowOption } from './incidentQuickSend';

function allowsInternalSubtasks(changeType: string, policy?: ChangeTypePolicyFlags): boolean {
  if (policy) return policy.enable_internal_subtasks;
  return policyFlagsForType(changeType, []).enable_internal_subtasks;
}

export function internalReviewKey(groupId: number, templateId?: number | null): string {
  return `${groupId}:${templateId && templateId > 0 ? templateId : 0}`;
}

export function parseInternalReviewKey(key: string): { groupId: number; templateId: number } {
  const base = String(key || '').split('~')[0];
  const [groupPart, templatePart] = base.split(':');
  return { groupId: Number(groupPart) || 0, templateId: Number(templatePart) || 0 };
}

/** Chave de linha na revisão (permite várias externas do mesmo group_id). */
export function externalLineKey(group: Pick<ChangeWorkGroup, 'group_id' | 'review_key'>): string {
  return group.review_key || String(group.group_id);
}

export function parseExternalLineKey(key: string): { groupId: number } {
  const base = String(key || '').split('~')[0];
  return { groupId: Number(base) || 0 };
}

export function nextDuplicateReviewKey(existingKeys: string[], baseKey: string): string {
  if (!existingKeys.includes(baseKey)) return baseKey;
  return `${baseKey}~${Date.now()}`;
}

/**
 * Resolve slot pelo review_key, inclusive entrada manual «grupo:0» que não entra em flattenInternalSlots
 * quando o grupo já tem modelos cadastrados.
 */
export function resolveInternalSlotByReviewKey(
  groups: ChangeWorkGroup[],
  reviewKey: string,
): ChangeWorkGroup | undefined {
  const normalized = String(reviewKey || '').split('~')[0];
  const fromFlat = flattenInternalSlots(groups).find((item) => slotReviewKey(item) === normalized);
  if (fromFlat) return fromFlat;
  const { groupId, templateId } = parseInternalReviewKey(normalized);
  if (!groupId) return undefined;
  const group = groups.find((item) => item.group_id === groupId);
  if (!group) return undefined;
  if (templateId > 0) {
    const tpl = (group.internal_templates || []).find((item) => item.id === templateId);
    if (tpl) {
      return {
        ...group,
        review_key: reviewKey,
        internal_subtask_template_id: tpl.id,
        internal_subtask_template_name: tpl.name,
        internal_templates: [{ ...tpl }],
        checklist_field_count: tpl.field_count ?? 0,
        show_on_dispatch: tpl.show_on_dispatch,
      };
    }
    if (group.internal_subtask_template_id === templateId) {
      return { ...group, review_key: reviewKey };
    }
    return undefined;
  }
  return {
    ...group,
    review_key: reviewKey,
    internal_subtask_template_id: undefined,
  };
}

export function expandInternalSlots(group: ChangeWorkGroup): ChangeWorkGroup[] {
  const templates = group.internal_templates || [];
  if (templates.length) {
    return templates.map((tpl) => ({
      ...group,
      internal_templates: [{ ...tpl }],
      review_key: internalReviewKey(group.group_id, tpl.id),
      internal_subtask_template_id: tpl.id,
      internal_subtask_template_name: tpl.name,
      checklist_field_count: tpl.field_count ?? 0,
      show_on_dispatch: tpl.show_on_dispatch,
    }));
  }
  if (group.internal_subtask_template_id || Number(group.checklist_field_count || 0) > 0) {
    return [
      {
        ...group,
        review_key: internalReviewKey(group.group_id, group.internal_subtask_template_id),
      },
    ];
  }
  return [];
}

export function flattenInternalSlots(groups: ChangeWorkGroup[]): ChangeWorkGroup[] {
  return groups.flatMap(expandInternalSlots);
}

/** Linhas do pacote/revisão na ordem das chaves (inclui entrada manual grupo:0). */
export function internalSlotsForReviewKeys(
  groups: ChangeWorkGroup[],
  reviewKeys: string[],
  opts?: { allowDuplicateLines?: boolean },
): ChangeWorkGroup[] {
  const out: ChangeWorkGroup[] = [];
  const seen = new Set<string>();
  reviewKeys.forEach((key) => {
    const slot = resolveInternalSlotByReviewKey(groups, key);
    if (!slot) return;
    const resolvedKey = slotReviewKey(slot);
    if (!opts?.allowDuplicateLines) {
      if (seen.has(resolvedKey)) return;
      seen.add(resolvedKey);
      out.push(slot);
      return;
    }
    out.push({ ...slot, review_key: key });
  });
  return out;
}

/** Uma entrada por área ativa — fila e fluxo na edição do chamado (validados ao salvar). */
export function manualPackageExternalOptions(groups: ChangeWorkGroup[]): ChangeWorkGroup[] {
  return groups
    .filter((group) => canAddReviewArea(group))
    .sort((left, right) => (left.name || '').localeCompare(right.name || '', 'pt-BR'));
}

/** Uma entrada por área ativa — repetição é na lista (várias linhas), não no combo. */
export function manualPackageInternalOptions(groups: ChangeWorkGroup[]): ChangeWorkGroup[] {
  return groups
    .filter((group) => canAddReviewArea(group))
    .map((group) => ({
      ...group,
      review_key: internalReviewKey(group.group_id, 0),
      internal_subtask_template_id: undefined,
      internal_subtask_template_name: undefined,
    }))
    .sort((left, right) => (left.name || '').localeCompare(right.name || '', 'pt-BR'));
}

/** Slot manual por área (checklist desta inclusão, sem fixar modelo do cadastro). */
export function manualInternalSlotForGroup(group: ChangeWorkGroup): ChangeWorkGroup {
  return {
    ...group,
    review_key: internalReviewKey(group.group_id, 0),
    internal_subtask_template_id: undefined,
    internal_subtask_template_name: undefined,
    internal_templates: undefined,
  };
}

export function slotReviewKey(slot: ChangeWorkGroup): string {
  return slot.review_key || internalReviewKey(slot.group_id, slot.internal_subtask_template_id);
}

export function internalSlotLabel(slot: ChangeWorkGroup): string {
  const templateName = (slot.internal_subtask_template_name || '').trim();
  const groupName = (slot.name || '').trim();
  if (templateName && groupName && templateName !== groupName) {
    return `${templateName} · ${groupName}`;
  }
  return templateName || groupName || 'Subtarefa interna';
}

export function slotShowsOnDispatch(slot: ChangeWorkGroup): boolean {
  const template = (slot.internal_templates || [])[0];
  if (template && typeof template.show_on_dispatch === 'boolean') {
    return template.show_on_dispatch;
  }
  if (typeof slot.show_on_dispatch === 'boolean') {
    return slot.show_on_dispatch;
  }
  return Boolean(slot.auto_subtask_on_normal);
}

function resolvedTemplateId(slot: ChangeWorkGroup): number {
  const fromSlot = slot.internal_subtask_template_id;
  if (typeof fromSlot === 'number' && fromSlot > 0) return fromSlot;
  const tpl = (slot.internal_templates || [])[0];
  return tpl?.id && tpl.id > 0 ? tpl.id : 0;
}

/**
 * Modelo cadastrado com «Mostrar na modal de disparo» entra mesmo sem campos (monta na edição).
 * Legado (checklist do grupo / auto) exige ao menos um campo.
 */
export function slotQualifiesForDispatchModal(slot: ChangeWorkGroup): boolean {
  if (!slotShowsOnDispatch(slot)) return false;
  const templateId = resolvedTemplateId(slot);
  if (templateId > 0) return true;
  return Number(slot.checklist_field_count || 0) > 0;
}

export function isCreateReviewInternal(
  group: ChangeWorkGroup,
  changeType: string,
  skipChecklist: boolean,
  policy?: ChangeTypePolicyFlags,
): boolean {
  if (skipChecklist) return false;
  if (!allowsInternalSubtasks(changeType, policy)) return false;
  return expandInternalSlots(group).some(
    (slot) => slotShowsOnDispatch(slot) && Number(slot.checklist_field_count || 0) > 0,
  );
}

export function resolveReviewIncidentWorkflow(
  group: ChangeWorkGroup,
  workflows: IncidentWorkflowOption[] = [],
): number | '' {
  const saved = group.incidents_workflow_id || null;
  if (!workflows.length) {
    return saved || '';
  }
  return defaultIncidentWorkflowId(group.incidents_group_id ?? undefined, workflows, saved) || '';
}

export function canAddInternalChecklist(
  group: ChangeWorkGroup,
  changeType: string,
  policy?: ChangeTypePolicyFlags,
): boolean {
  if (!allowsInternalSubtasks(changeType, policy)) return false;
  return expandInternalSlots(group).some((slot) => Number(slot.checklist_field_count || 0) > 0);
}

export function canAddReviewArea(group: ChangeWorkGroup): boolean {
  return group.is_active !== false;
}

/** Área ativa com fila de incidentes: entra em «Adicionar chamado» mesmo sem fluxo resolvido. */
export function canAddReviewExternal(group: ChangeWorkGroup): boolean {
  return canAddReviewArea(group) && Boolean(group.open_in_incidents && group.incidents_group_id);
}

/** Área elegível no combo «Adicionar chamado» (fila + fluxo resolvível). */
export function canPickExternalForReview(
  group: ChangeWorkGroup,
  workflows: IncidentWorkflowOption[] = [],
): boolean {
  if (!canAddReviewExternal(group)) return false;
  return Boolean(resolveReviewIncidentWorkflow(group, workflows));
}

export function isCreateReviewExternal(
  group: ChangeWorkGroup,
  workflows: IncidentWorkflowOption[] = [],
): boolean {
  if (!group.open_in_incidents || !group.incidents_group_id) return false;
  return Boolean(resolveReviewIncidentWorkflow(group, workflows));
}

/** Pacote pré-preenchido no cadastro da área (aba Externa). */
export function hasIncidentCreatePackageDefaults(group: ChangeWorkGroup): boolean {
  const title = (group.incident_title_default || '').trim();
  const description = (group.incident_description_default || '').trim();
  const raw = group.incident_custom_field_defaults;
  const hasCustom =
    raw != null &&
    typeof raw === 'object' &&
    Object.keys(raw as Record<string, unknown>).length > 0;
  return Boolean(title || description || hasCustom);
}

/**
 * Chamados sugeridos ao criar a mudança (passo Confirme as subtarefas).
 * CINFRA (gate Transporte/Implementar) não entra sozinho — use a aba Transporte depois.
 */
export function isCreateReviewExternalSuggested(
  group: ChangeWorkGroup,
  workflows: IncidentWorkflowOption[] = [],
): boolean {
  if (!isCreateReviewExternal(group, workflows)) return false;
  if (isInfraIncidentArea(group)) return false;
  return hasIncidentCreatePackageDefaults(group);
}

export function subtaskOccupiesGroup(
  subtask: Pick<ChangeSubtask, 'group_id' | 'invalidated_at'>,
): boolean {
  if (!subtask.group_id) return false;
  return !subtask.invalidated_at;
}

export function occupyingSubtaskGroupIds(
  subtasks?: Array<Pick<ChangeSubtask, 'group_id' | 'invalidated_at'>> | null,
): Set<number> {
  return new Set(
    (subtasks || []).filter(subtaskOccupiesGroup).map((item) => Number(item.group_id)),
  );
}

export function occupyingInternalReviewKeys(
  subtasks?: Array<
    Pick<ChangeSubtask, 'group_id' | 'internal_template_id' | 'origin' | 'invalidated_at'>
  > | null,
): Set<string> {
  return new Set(
    (subtasks || [])
      .filter((item) => subtaskOccupiesGroup(item) && item.origin !== 'incidente')
      .map((item) => internalReviewKey(item.group_id, item.internal_template_id)),
  );
}

export function occupyingExternalGroupIds(
  subtasks?: Array<Pick<ChangeSubtask, 'group_id' | 'origin' | 'invalidated_at'>> | null,
): Set<number> {
  return new Set(
    (subtasks || [])
      .filter((item) => subtaskOccupiesGroup(item) && item.origin === 'incidente')
      .map((item) => Number(item.group_id)),
  );
}

export function sortSubtasksActiveFirst<T extends { invalidated_at?: string | null }>(
  items: T[],
): T[] {
  const active: T[] = [];
  const canceled: T[] = [];
  items.forEach((item) => {
    if (item.invalidated_at) canceled.push(item);
    else active.push(item);
  });
  return active.concat(canceled);
}

/** Internas com «Mostrar na modal de disparo» (modelo cadastrado ou legado com campos). */
export function suggestedDispatchInternalSlots(
  groups: ChangeWorkGroup[],
  opts: {
    changeType: string;
    skipChecklist?: boolean;
    policy?: ChangeTypePolicyFlags;
    allowInternal: boolean;
    excludeKeys?: Set<string>;
  },
): ChangeWorkGroup[] {
  if (!opts.allowInternal) return [];
  if (opts.skipChecklist) return [];
  if (!allowsInternalSubtasks(opts.changeType, opts.policy)) return [];
  const exclude = opts.excludeKeys ?? new Set<string>();
  return flattenInternalSlots(groups).filter(
    (slot) => slotQualifiesForDispatchModal(slot) && !exclude.has(slotReviewKey(slot)),
  );
}

/** Chamados sugeridos no passo «Confirme as subtarefas» (pacote CSI + demais filas elegíveis). */
export function suggestedDispatchExternalGroups(
  groups: ChangeWorkGroup[],
  workflows: IncidentWorkflowOption[],
  opts: { allowExternal: boolean; excludeGroupIds?: Set<number> },
): ChangeWorkGroup[] {
  if (!opts.allowExternal) return [];
  const exclude = opts.excludeGroupIds ?? new Set<number>();
  const suggested = groups.filter(
    (group) =>
      isCreateReviewExternalSuggested(group, workflows) && !exclude.has(group.group_id),
  );
  const suggestedIds = new Set(suggested.map((item) => item.group_id));
  const leftover = groups.filter(
    (group) =>
      canAddReviewArea(group) &&
      isCreateReviewExternal(group, workflows) &&
      !suggestedIds.has(group.group_id) &&
      !exclude.has(group.group_id),
  );
  return [...suggested, ...leftover];
}

/** Áreas que a criação pré-preenche no passo «Confirme as subtarefas». */
export function createLikePackageGroupIds(
  groups: ChangeWorkGroup[],
  opts: {
    changeType: string;
    allowInternal: boolean;
    allowExternal: boolean;
    workflows: IncidentWorkflowOption[];
    occupiedGroupIds: Set<number>;
    occupiedInternalKeys?: Set<string>;
    occupiedExternalIds?: Set<number>;
    policy?: ChangeTypePolicyFlags;
  },
): { internalIds: number[]; internalKeys: string[]; externalIds: number[] } {
  const occupiedInternal = opts.occupiedInternalKeys || new Set<string>();
  const occupiedExternal = opts.occupiedExternalIds || opts.occupiedGroupIds;
  const internalSlots = suggestedDispatchInternalSlots(groups, {
    changeType: opts.changeType,
    policy: opts.policy,
    allowInternal: opts.allowInternal,
    excludeKeys: occupiedInternal,
  });
  const externalGroups = suggestedDispatchExternalGroups(groups, opts.workflows, {
    allowExternal: opts.allowExternal,
    excludeGroupIds: occupiedExternal,
  });
  return {
    internalIds: internalSlots.map((slot) => slot.group_id),
    internalKeys: internalSlots.map((slot) => slotReviewKey(slot)),
    externalIds: externalGroups.map((group) => group.group_id),
  };
}

export function leftoverGroups(
  groups: ChangeWorkGroup[],
  includedIds: number[],
  eligible: (group: ChangeWorkGroup) => boolean,
): ChangeWorkGroup[] {
  const seen = new Set(includedIds);
  return groups.filter((group) => eligible(group) && !seen.has(group.group_id));
}

export function leftoverInternalSlots(
  groups: ChangeWorkGroup[],
  includedKeys: string[],
  eligible: (slot: ChangeWorkGroup) => boolean,
): ChangeWorkGroup[] {
  const seen = new Set(includedKeys);
  return flattenInternalSlots(groups).filter(
    (slot) => eligible(slot) && !seen.has(slotReviewKey(slot)),
  );
}

/**
 * Opções para «Adicionar interna»: modelos com disparo ainda não na lista + **todas** as áreas
 * ativas (entrada manual por grupo), para não esconder filas que já têm modelo.
 */
export function leftoverManualInternalSlots(
  groups: ChangeWorkGroup[],
  includedKeys: string[],
  opts: {
    allowInternal: boolean;
    changeType?: string;
    policy?: ChangeTypePolicyFlags;
    /** Pacote manual na aba Subtarefas: ignora política do tipo (disparo na criação continua separado). */
    ignoreTypeInternalPolicy?: boolean;
  },
): ChangeWorkGroup[] {
  if (!opts.allowInternal) return [];
  if (
    opts.changeType &&
    !opts.ignoreTypeInternalPolicy &&
    !allowsInternalSubtasks(opts.changeType, opts.policy)
  ) {
    return [];
  }
  const seen = new Set(includedKeys);
  const options = new Map<string, ChangeWorkGroup>();

  for (const slot of flattenInternalSlots(groups)) {
    if (!canAddReviewArea(slot)) continue;
    const key = slotReviewKey(slot);
    if (seen.has(key)) continue;
    const templateId = resolvedTemplateId(slot);
    const include =
      templateId > 0 ? slotShowsOnDispatch(slot) : slotQualifiesForDispatchModal(slot);
    if (!include) continue;
    options.set(key, slot);
  }

  for (const group of groups) {
    if (!canAddReviewArea(group)) continue;
    const manualKey = internalReviewKey(group.group_id, 0);
    if (seen.has(manualKey) || options.has(manualKey)) continue;
    options.set(manualKey, {
      ...group,
      review_key: manualKey,
      internal_subtask_template_id: undefined,
    });
  }

  return Array.from(options.values()).sort((left, right) =>
    internalSlotLabel(left).localeCompare(internalSlotLabel(right), 'pt-BR'),
  );
}

export function reviewChipLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
